import { useEffect, useReducer, useRef } from 'react';
import { Modal as ReactstrapModal } from 'reactstrap';

const stopEscape = (e) => {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
  }
};

/**
 * Wrapped for reactstrap Modal.
 *
 * The reactstrap Modal captures `keyup` for Escape but not `keydown`. This
 * causes issues with `react-hotkeys-hook`, since it records an Escape keypress
 * on `keydown` but never receives the `keyup`, causing the Escape key to
 * "persist" in the hotkeys state. To fix this, we also capture `keydown` here
 * and prevent propagation.
 */
const Modal = (props) => {
  const { isOpen } = props;

  const modalRef = useRef(null);

  /**
   * We can't just set an event listener on Window, since that won't catch the
   * event before it bubbles up to the hotkeys listener. We need to set the
   * listener on the Modal's element. To access the innerRef of the Modal, we
   * need to run this effect after the Modal has been opened, so we force an
   * update to achieve this.
   */
  const [state, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const modalElement = modalRef.current;

    if (isOpen && modalElement) {
      modalElement.addEventListener('keydown', stopEscape);
      return () => modalElement.removeEventListener('keydown', stopEscape);
    }

    return () => {};
  }, [isOpen, state]);

  return (
    <ReactstrapModal {...props} innerRef={modalRef} onOpened={forceUpdate} />
  );
};

export default Modal;
